import { ComboboxProvider, TabProvider, useDialogStore } from '@ariakit/react';
import { useModalContext } from '@chakra-ui/react';
import { useStableCallback } from '@main/shared/browser';
import { isNonNullable } from '@main/shared/utils';
import useDebounceFn from 'ahooks/es/useDebounceFn';
import { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react';

import { useOptional } from '../hooks';

export interface GlobalSearchState {
  query: string;
  category?: string;
}

export interface GlobalSearchResult {
  id: string;
  category?: string;
}

export interface GlobalSearchContextValue<TResult extends GlobalSearchResult = GlobalSearchResult>
  extends GlobalSearchProviderProps<TResult> {
  resultsGroups: Partial<Record<string, TResult[]>>;
  resultsTab?: TResult[];
  tabs: string[];
  tabId: string;
  isNoResults: boolean;
  hasResults: boolean;
  onLoadMore: () => void;
}

export const GlobalSearchContext = createContext<GlobalSearchContextValue | undefined>(undefined);

export function useGlobalSearchContext<TResult extends GlobalSearchResult>() {
  const context = useContext(GlobalSearchContext);

  if (!context) {
    throw new Error('useGlobalSearchContext must be used within a GlobalSearchProvider');
  }

  return context as unknown as GlobalSearchContextValue<TResult>;
}

const ALL_CATEGORY = 'All';

export interface GlobalSearchProviderProps<TResult extends GlobalSearchResult> {
  results?: TResult[];
  initialSearch?: string;
  initialCategory?: string;
  debounce?: number;
  selectedResults?: TResult[];
  categories?: string[];
  isSearching?: boolean;
  isFailed?: boolean;
  prefix?: string;
  onSearch?: (state: GlobalSearchState) => void;
  onSelect?: (result: TResult) => void;
  onSelectionUpdate?: (results: TResult[]) => void;
}

export function GlobalSearchProvider<TResult extends GlobalSearchResult>({
  children,
  ...props
}: PropsWithChildren<GlobalSearchProviderProps<TResult>>) {
  const prefix = props.prefix ?? 'global-search-';
  const modal = useOptional(useModalContext);

  const dialogStore = useDialogStore({ open: modal?.isOpen ?? true });

  const [searchQuery, setSearchQuery] = useState(props.initialSearch ?? '');
  const [tabId, setTabId] = useState(props.initialCategory ?? ALL_CATEGORY);
  const tabs = useMemo(() => [ALL_CATEGORY, ...(props.categories ?? [])], [props.categories]);

  const resultsGroups = useMemo(
    () => Object.groupBy(props.results ?? [], (result) => result.category ?? ALL_CATEGORY),
    [props.results],
  );

  const resultsTab = useMemo(
    () => (tabId === ALL_CATEGORY ? props.results : resultsGroups[tabId]),
    [tabId, props.results, resultsGroups],
  );
  const isNoResults =
    resultsTab?.length === 0 || props.results?.length === 0 || (!!props.results && !resultsTab);
  const hasResults = !!resultsTab && resultsTab?.length > 0;

  const asTabId = useStableCallback((tab: string) => `${prefix}${tab}`);
  const asTab = useStableCallback((tabId: string) => tabId.replace(prefix, ''));
  const onSearch = useStableCallback(props.onSearch);
  const { run: debouncedOnSearch, cancel: cancelDebouncedSearch } = useDebounceFn(
    (state: GlobalSearchState) => onSearch?.(state),
    { wait: props.debounce ?? 300 },
  );
  const onTabChange = useStableCallback((tab?: string | null) => {
    if (!dialogStore.getState().open) return;
    const newTab = asTab(tab ?? ALL_CATEGORY);
    setTabId(newTab);
    cancelDebouncedSearch();
    onSearch?.({
      query: searchQuery,
      category: newTab !== ALL_CATEGORY ? newTab : undefined,
    });
  });
  const onSelect = useStableCallback(props.onSelect);
  const onSelectionUpdate = useStableCallback(props.onSelectionUpdate);

  const resultsMap = useMemo(
    () => Object.fromEntries(props.results?.map((result) => [result.id, result]) ?? []),
    [props.results],
  );

  const selectedResultsMap = useMemo(
    () => Object.fromEntries(props.selectedResults?.map((result) => [result.id, result]) ?? []),
    [props.selectedResults],
  );
  const selectedValue = useMemo(
    () => props.selectedResults?.map((result) => result.id),
    [props.selectedResults],
  );

  const onSearchChange = useStableCallback((value: string) => {
    if (!resultsMap[value]) {
      setSearchQuery(value);
      if (!value) {
        cancelDebouncedSearch();
        onSearch?.({ query: '' });
      } else {
        debouncedOnSearch({
          query: value,
          category: tabId !== ALL_CATEGORY ? tabId : undefined,
        });
      }
    }
  });

  const onSelectedValue = useStableCallback((value: string | string[]) => {
    if (Array.isArray(value)) {
      const results = value
        .map((option) => resultsMap[option] ?? selectedResultsMap[option])
        .filter(isNonNullable);
      onSelectionUpdate(results);
    } else {
      const result = resultsMap[value];

      if (result) {
        onSelect(result);
      }
    }
  });

  const onLoadMore = useStableCallback(() => {
    onSearch?.({
      query: searchQuery,
      category: tabId !== ALL_CATEGORY ? tabId : undefined,
    });
  });

  const context: GlobalSearchContextValue<TResult> = useMemo(
    () => ({
      results: props.results,
      initialSearch: props.initialSearch,
      initialCategory: props.initialCategory,
      debounce: props.debounce,
      selectedResults: props.selectedResults,
      categories: props.categories,
      isSearching: props.isSearching,
      isFailed: props.isFailed,
      onSelect: props.onSelect,
      onSelectionUpdate: props.onSelectionUpdate,
      prefix,
      resultsGroups,
      resultsTab,
      tabs,
      tabId: asTabId(tabId),
      isNoResults,
      hasResults,
      onLoadMore,
      onSearch,
    }),
    [
      props.results,
      props.initialSearch,
      props.initialCategory,
      props.debounce,
      props.selectedResults,
      props.categories,
      props.isSearching,
      props.isFailed,
      props.onSelect,
      props.onSelectionUpdate,
      prefix,
      resultsGroups,
      resultsTab,
      tabs,
      asTabId,
      tabId,
      isNoResults,
      hasResults,
      onLoadMore,
      onSearch,
    ],
  );

  return (
    <GlobalSearchContext.Provider value={context as unknown as GlobalSearchContextValue}>
      <ComboboxProvider
        disclosure={dialogStore}
        selectedValue={selectedValue}
        defaultSelectedValue={props.onSelectionUpdate ? [] : undefined}
        setSelectedValue={onSelectedValue}
        value={searchQuery}
        setValue={onSearchChange}
        resetValueOnHide={false}
      >
        <TabProvider selectedId={asTabId(tabId)} setSelectedId={onTabChange}>
          {children}
        </TabProvider>
      </ComboboxProvider>
    </GlobalSearchContext.Provider>
  );
}
